import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Users, MessageSquare, CalendarDays, FileText, CheckCircle2, Lock, Crown } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { DEFAULT_PROJECT_ID } from '@/lib/navTree';
import ClaimFlatCard from '@/components/allottees/ClaimFlatCard';

const API = process.env.REACT_APP_BACKEND_URL;

// Clearly labelled SAMPLE screens: shows how the group works, never real members or posts.
const SAMPLES = [
  { icon: Users, title: 'Member directory', lines: ['A. S. · Wing B · verified owner', 'R. K. · Wing D · verified owner', 'M. P. · Wing A · verification pending'] },
  { icon: MessageSquare, title: 'Issue threads', lines: ['Corpus shortfall: 23 owners endorsed', 'Parking allotment vs agreement: 9 replies', 'Lift maintenance contract: open'] },
  { icon: CalendarDays, title: 'Meetings & minutes', lines: ['Notice: Owners meeting, Sat 10 am, clubhouse', 'Agenda: corpus, conveyance, society formation', 'Minutes and decisions filed after the meeting'] },
  { icon: FileText, title: 'Shared documents', lines: ['Developer letters & replies', 'Corpus statements', 'Legal notices sent on behalf of owners'] },
];

const FREE = [
  { icon: CheckCircle2, who: 'Free, no sign-in', what: 'Project records, MahaRERA documents, the complaint register and original orders' },
  { icon: CheckCircle2, who: 'Free with sign-in', what: 'Follow alerts, S.18 and subvention registration, your own documents' },
  { icon: Lock, who: 'Free after ownership is verified', what: "The Buyers' Group: directory, threads, meetings, notices and shared documents" },
  { icon: Crown, who: 'Paid (FBC Project Intelligence)', what: 'AI case briefs, financial analysis and detailed record reviews' },
];

const Counts = ({ projectId }) => {
  const [g, setG] = useState(null);
  useEffect(() => { axios.get(`${API}/api/buyers-group/glimpse/${projectId}`).then((r) => setG(r.data)).catch(() => {}); }, [projectId]);
  if (!g) return null;
  const items = [[g.verified_owners, 'verified owners'], [g.documents_shared, 'documents shared'], [g.open_issues, 'open issues'],
    [g.contributions, 'contributions'], [g.meetings, 'meetings held']].filter(([n]) => n > 0);
  if (!items.length) return null;
  return (
    <p className="text-sm" data-testid="group-glimpse-counts">
      <span className="font-semibold">{projectId === DEFAULT_PROJECT_ID ? 'TEN BKC' : 'Your project'}'s group today: </span>
      {items.map(([n, l]) => `${n} ${l}`).join(' · ')}
    </p>
  );
};

export default function GroupGlimpse() {
  const { user } = useAuth();
  const pid = user?.project_id || DEFAULT_PROJECT_ID;
  return (
    <div className="space-y-4" data-testid="group-glimpse">
      <ClaimFlatCard projectId={pid} />
      <Counts projectId={pid} />
      <div>
        <div className="text-sm font-semibold mb-2 flex items-center gap-1">What members see <Badge variant="outline" className="text-[10px]">SAMPLE</Badge></div>
        <div className="grid sm:grid-cols-2 gap-3">
          {SAMPLES.map((s) => (
            <Card key={s.title} className="relative overflow-hidden" data-testid={`glimpse-sample-${s.title.split(' ')[0].toLowerCase()}`}>
              <span className="absolute top-2 right-2 text-[9px] font-bold tracking-widest text-muted-foreground/60">SAMPLE</span>
              <CardContent className="p-3">
                <p className="text-sm font-medium flex items-center gap-1.5 mb-1.5"><s.icon className="h-4 w-4 text-green-700" />{s.title}</p>
                <ul className="space-y-1">{s.lines.map((l) => <li key={l} className="text-xs text-muted-foreground rounded bg-muted/50 px-2 py-1">{l}</li>)}</ul>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">Illustrative content. Real members, posts and documents are visible only to verified owners of the project.</p>
      </div>
      <Card className="border-green-200" data-testid="group-free-explainer">
        <CardContent className="p-3">
          <p className="text-sm font-semibold mb-2">What is free, exactly?</p>
          <ul className="space-y-1.5">
            {FREE.map((f) => (
              <li key={f.who} className="flex items-start gap-2 text-xs"><f.icon className="h-3.5 w-3.5 mt-0.5 text-green-700 shrink-0" /><span><span className="font-semibold">{f.who}: </span>{f.what}</span></li>
            ))}
          </ul>
        </CardContent>
      </Card>
    </div>
  );
}
